import * as React from 'react';
import classNames from 'classnames';
import { chatEventEmitter, ChatRoom, chatSdk, Message } from '@wix/chat-sdk';
import {
  AVAILABILITY_STATUS,
  Avatar as IAvatar,
  AvatarType,
  expandCollapseManager,
  ImageAvatar,
  isNoneAvatar,
  MessageNotification,
  NoneAvatar,
} from '@wix/chat-web';
import styles from './MessagePopup.scss';
import wix from '../../services/wix';
import _get from 'lodash/get';
import Avatar from '../Avatar/Avatar';
import ThemeProvider from '@wix/chat-web/dist/src/providers/theme/ThemeProvider';
import { connect } from 'react-redux';
import { editorSettingsAvatar, theme } from '../../reducers';
import { updateUnreadCount } from '../../actions';

export interface MessagePopupProps {
  theme: any;
  locale: string;
  editorSettingsLayout: any;
  deviceType: string;
  editorSettingsAvatar: any;
  widgetExpanded: boolean;
  unreadCount: number;
  businessChatRoomId: string;
  activeRoom?: string;
  updateUnreadCount(unreadCount: number): void;
  onClick: Function;
}

interface MessagePopupState {
  newMessages: Message[];
  shouldShowNotification: boolean;
  lastMessageChatroom: ChatRoom | undefined;
}

class _MessagePopup extends React.Component<
  MessagePopupProps,
  MessagePopupState
> {
  autoCloseTimeoutId;

  readonly state = {
    newMessages: [] as Message[],
    shouldShowNotification: false,
    lastMessageChatroom: undefined,
  };

  componentDidMount() {
    const { deviceType } = this.props;

    if (deviceType !== 'desktop') {
      return;
    }

    this.subscribeToNewMessages();
    this.handleCollapseExpand();
  }

  onExpand = () => {
    this.clearAwaitingAutoClose();
    this.setState({
      newMessages: [],
      shouldShowNotification: false,
      lastMessageChatroom: undefined,
    });
    this.props.updateUnreadCount(0);
  };

  componentWillUnmount() {
    this.clearAwaitingAutoClose();
    this.unsubscribeFromNewMessages();

    expandCollapseManager.unregisterOnExpand(this.onExpand);
  }

  handleCollapseExpand() {
    expandCollapseManager.registerOnExpand(this.onExpand);
  }

  clearAwaitingAutoClose = () => {
    if (this.autoCloseTimeoutId) {
      window.clearTimeout(this.autoCloseTimeoutId);
      this.autoCloseTimeoutId = undefined;
    }
  };

  handleAutoClose = () => {
    this.clearAwaitingAutoClose();

    const timeout = 5000;
    this.autoCloseTimeoutId = window.setTimeout(() => {
      this.onClose();
    }, timeout);
  };

  onNewMessage = async messageDto => {
    const message = new Message(messageDto);

    if (
      this.state.newMessages.some(
        (aMessage: Message) => aMessage.id === message.id,
      )
    ) {
      return;
    }

    const isSameRoom =
      !this.props.activeRoom || this.props.activeRoom === message.chatroomId;

    if (this.props.widgetExpanded && isSameRoom) {
      return;
    }

    const chatroomId = message.chatroomId;
    let lastMessageChatroom;
    try {
      lastMessageChatroom =
        this.props.businessChatRoomId === chatroomId
          ? undefined
          : new ChatRoom(
              await chatSdk.getChatroom({
                chatroomId,
              }),
            );
    } catch (e) {
      console.error('Error getting chat room-', e);
      lastMessageChatroom = undefined;
    }

    const newMessages = [...this.state.newMessages, message];

    this.setState({
      newMessages,
      shouldShowNotification: true,
      lastMessageChatroom,
    });
    if (this.props.unreadCount > 0) {
      this.props.updateUnreadCount(newMessages.length);
    }

    this.handleAutoClose();

    try {
      if (this.props.widgetExpanded) {
        await wix.resizeToExpandedWithMessageNotification();
      } else {
        await wix.resizeToCollapsedWithMessageNotification();
      }
    } catch (e) {}
  };

  subscribeToNewMessages() {
    chatEventEmitter.addListener(
      chatEventEmitter.CHAT_EVENTS.NEW_REALTIME_MESSAGE,
      this.onNewMessage,
    );
  }

  unsubscribeFromNewMessages() {
    chatEventEmitter.removeListener(
      chatEventEmitter.CHAT_EVENTS.NEW_REALTIME_MESSAGE,
      this.onNewMessage,
    );
  }

  onClick = () => {
    const lastMessage = this.state.newMessages[
      this.state.newMessages.length - 1
    ];
    this.props.onClick && this.props.onClick(lastMessage);

    this.setState({
      newMessages: [],
      shouldShowNotification: false,
      lastMessageChatroom: undefined,
    });
  };

  onClose = () => {
    const { newMessages } = this.state;

    this.clearAwaitingAutoClose();

    if (!this.props.unreadCount) {
      this.props.updateUnreadCount(newMessages.length);
    }

    this.setState({ shouldShowNotification: false });

    const asRounded = _get(
      this.props,
      'editorSettingsLayout.collapsedAsRounded',
      false,
    );

    if (this.props.widgetExpanded) {
      void wix.resizeToExpandedMode();
    } else {
      void wix.resizeToCollapsedMode(asRounded);
    }
  };

  getAvatar = (): IAvatar | undefined => {
    const { newMessages } = this.state;
    const { lastMessageChatroom } = this.state;

    const lastMessage = newMessages[newMessages.length - 1];

    if (this.isBusinessRoom()) {
      if (!isNoneAvatar(this.props.editorSettingsAvatar)) {
        return this.props.editorSettingsAvatar;
      }
      if (
        lastMessage.sender &&
        lastMessage.sender.type === 'contact' &&
        lastMessage.senderDisplayData &&
        lastMessage.senderDisplayData.image &&
        lastMessage.senderDisplayData.image.url
      ) {
        return {
          avatarType: AvatarType.Image,
          imgUrl: lastMessage.senderDisplayData.image.url,
        } as ImageAvatar;
      }
      return {
        avatarType: AvatarType.None,
      } as NoneAvatar;
    }

    // @ts-ignore: all
    const imgUrl = lastMessageChatroom ? lastMessageChatroom.thumbnail : '';
    return {
      avatarType: AvatarType.Image,
      imgUrl,
    } as ImageAvatar;
  };

  private isBusinessRoom() {
    const { lastMessageChatroom, newMessages } = this.state;
    const { businessChatRoomId } = this.props;

    const lastMessage = newMessages[newMessages.length - 1];

    return businessChatRoomId === lastMessage.chatroomId;
  }

  private getTitle() {
    const { newMessages, lastMessageChatroom } = this.state;
    const lastMessage = newMessages[newMessages.length - 1];

    const editorSettingsTexts = _get(this.props, 'editorSettingsTexts', {});
    const { onlineTitle, offlineTitle } = editorSettingsTexts;
    // @ts-ignore: all
    const chatroomName = lastMessageChatroom ? lastMessageChatroom.name : '';

    const isAvailable =
      _get(this.props, 'availabilityStatus') !== AVAILABILITY_STATUS.AWAY;
    const lastSenderName =
      lastMessage.senderDisplayData && lastMessage.senderDisplayData.name;

    if (this.isBusinessRoom()) {
      return isAvailable ? onlineTitle : offlineTitle;
    }

    // @ts-ignore: all
    if (lastMessageChatroom && lastMessageChatroom.isPrivate) {
      return `${chatroomName}`;
    }

    return `${lastSenderName}@${chatroomName}`;
  }

  render() {
    const { newMessages, shouldShowNotification } = this.state;

    if (!shouldShowNotification) {
      return null;
    }

    const avatar = this.getAvatar();
    const asRounded = _get(
      this.props.editorSettingsLayout,
      'collapsedAsRounded',
      false,
    );

    const isExpanded = this.props.widgetExpanded;
    const lastMessage = newMessages[newMessages.length - 1];
    const isSameRoom =
      !this.props.activeRoom ||
      this.props.activeRoom === lastMessage.chatroomId;
    const isCompact = isExpanded && !isSameRoom;

    return (
      <div
        data-hook="message-popup"
        className={classNames(styles.messagePopup, {
          [styles.asRounded]: asRounded,
          [styles.isExpanded]: isExpanded,
        })}
      >
        <ThemeProvider theme={this.props.theme}>
          <MessageNotification
            messages={isCompact ? [lastMessage] : newMessages}
            onClick={() => this.onClick()}
            onClose={() => this.onClose()}
            avatar={avatar ? <Avatar size={30} avatar={avatar} /> : undefined}
            title={this.getTitle()}
            withCallToAction={!isCompact}
            locale={this.props.locale}
          />
        </ThemeProvider>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  theme: theme(state),
  editorSettingsAvatar: editorSettingsAvatar(state),
  locale: state.locale,
  deviceType: state.deviceType,
  editorSettingsTexts: state.editorSettingsTexts,
  editorSettingsLayout: state.editorSettingsLayout,
  widgetExpanded: state.widgetExpanded,
  unreadCount: state.unreadCount,
  businessChatRoomId: state.businessChatRoomId,
});

const mapDispatchToProps = dispatch => ({
  updateUnreadCount: unreadCount => dispatch(updateUnreadCount(unreadCount)),
});

export const MessagePopup = connect(
  mapStateToProps,
  mapDispatchToProps,
)(_MessagePopup);
